'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DebtRatioCalculator() {
  const t = useTranslations("tools.debtRatioCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    salary: 5000,
    salaryPeriod: 'month',
    pension: 0,
    pensionPeriod: 'month',
    investment: 0,
    investmentPeriod: 'month',
    otherIncome: 0,
    otherIncomePeriod: 'month',
    rental: 1200,
    rentalPeriod: 'month',
    mortgage: 0,
    mortgagePeriod: 'month',
    propertyTax: 0,
    propertyTaxPeriod: 'month',
    hoa: 0,
    hoaPeriod: 'month',
    insurance: 0,
    insurancePeriod: 'month',
    creditCards: 200,
    creditCardsPeriod: 'month',
    studentLoan: 0,
    studentLoanPeriod: 'month',
    autoLoan: 250,
    autoLoanPeriod: 'month',
    otherLoan: 0,
    otherLoanPeriod: 'month',
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const toMonthly = (value: number, period: string) => {
    return period === 'year' ? value / 12 : value;
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const getDTIStatus = (ratio: number) => {
    if (ratio <= 20) return { text: t('statusExcellent'), class: 'excellent' };
    if (ratio <= 36) return { text: t('statusGood'), class: 'good' };
    if (ratio <= 43) return { text: t('statusFair'), class: 'fair' };
    return { text: t('statusPoor'), class: 'poor' };
  };

  const getRecommendations = (dti: number, frontEnd: number) => {
    const recommendations = [];

    if (dti <= 20) {
      recommendations.push(t('rec1Excellent'));
      recommendations.push(t('rec2Excellent'));
      recommendations.push(t('rec3Excellent'));
    } else if (dti <= 36) {
      recommendations.push(t('rec1Good'));
      recommendations.push(t('rec2Good'));
      recommendations.push(t('rec3Good'));
    } else if (dti <= 43) {
      recommendations.push(t('rec1Fair'));
      recommendations.push(t('rec2Fair'));
      recommendations.push(t('rec3Fair'));
      recommendations.push(t('rec4Fair'));
    } else {
      recommendations.push(t('rec1Poor'));
      recommendations.push(t('rec2Poor'));
      recommendations.push(t('rec3Poor'));
      recommendations.push(t('rec4Poor'));
      recommendations.push(t('rec5Poor'));
    }

    if (frontEnd > 28) {
      recommendations.push(t('recHousing'));
    }

    return recommendations;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const salary = toMonthly(inputs.salary, inputs.salaryPeriod);
    const pension = toMonthly(inputs.pension, inputs.pensionPeriod);
    const investment = toMonthly(inputs.investment, inputs.investmentPeriod);
    const otherIncome = toMonthly(inputs.otherIncome, inputs.otherIncomePeriod);

    const rental = toMonthly(inputs.rental, inputs.rentalPeriod);
    const mortgage = toMonthly(inputs.mortgage, inputs.mortgagePeriod);
    const propertyTax = toMonthly(inputs.propertyTax, inputs.propertyTaxPeriod);
    const hoa = toMonthly(inputs.hoa, inputs.hoaPeriod);
    const insurance = toMonthly(inputs.insurance, inputs.insurancePeriod);

    const creditCards = toMonthly(inputs.creditCards, inputs.creditCardsPeriod);
    const studentLoan = toMonthly(inputs.studentLoan, inputs.studentLoanPeriod);
    const autoLoan = toMonthly(inputs.autoLoan, inputs.autoLoanPeriod);
    const otherLoan = toMonthly(inputs.otherLoan, inputs.otherLoanPeriod);

    const totalIncome = salary + pension + investment + otherIncome;
    const housingExpenses = rental + mortgage + propertyTax + hoa + insurance;
    const otherDebts = creditCards + studentLoan + autoLoan + otherLoan;
    const totalDebt = housingExpenses + otherDebts;

    if (totalIncome === 0) {
      alert(t('alertIncome'));
      return;
    }

    const frontEndRatio = (housingExpenses / totalIncome) * 100;
    const backEndRatio = (totalDebt / totalIncome) * 100;

    const status = getDTIStatus(backEndRatio);
    const recommendations = getRecommendations(backEndRatio, frontEndRatio);

    setResults({
      dtiPercentage: backEndRatio.toFixed(1),
      frontEndRatio: frontEndRatio.toFixed(1),
      backEndRatio: backEndRatio.toFixed(1),
      status,
      totalIncome: formatCurrency(totalIncome),
      housingExpenses: formatCurrency(housingExpenses),
      otherDebts: formatCurrency(otherDebts),
      totalDebt: formatCurrency(totalDebt),
      recommendations,
    });

    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1e40af',
    secondary: '#7c3aed',
    accent: '#f59e0b',
    accentHover: '#d97706',
    textPrimary: '#1e293b',
    textSecondary: '#334155',
    textMuted: '#64748b',
    bgGradient1: '#f0f9ff',
    bgGradient2: '#e0f2fe',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
    statusExcellent: '#dcfce7',
    statusExcellentText: '#166534',
    statusGood: '#dbeafe',
    statusGoodText: '#1e40af',
    statusFair: '#fef3c7',
    statusFairText: '#92400e',
    statusPoor: '#fee2e2',
    statusPoorText: '#991b1b',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    summaryText: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      maxWidth: '800px',
      margin: '0 auto',
    },
    calculatorTool: {
      background: `linear-gradient(135deg, ${colors.bgGradient1} 0%, ${colors.bgGradient2} 100%)`,
      borderRadius: '1rem',
      marginBottom: '2rem',
      overflow: 'hidden' as const,
    },
    toolHeader: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      textAlign: 'center' as const,
    },
    toolHeaderH2: {
      margin: 0,
      fontWeight: 700,
    },
    calculatorForm: {
      background: 'white',
    },
    formSection: {
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontWeight: 700,
      color: colors.primary,
      marginBottom: '1rem',
      paddingBottom: '0.5rem',
      borderBottom: `2px solid ${colors.bgGradient2}`,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    formGrid: {
      display: 'grid',
      gap: '1.5rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: {
      fontWeight: 600,
      color: colors.textSecondary,
      marginBottom: '0.5rem',
      fontSize: '0.95rem',
    },
    inputWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    input: {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
    },
    select: {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '0.95rem',
      background: 'white',
      cursor: 'pointer',
      minWidth: '100px',
    },
    helperText: {
      fontSize: '0.8rem',
      color: colors.textMuted,
      marginTop: '0.25rem',
    },
    calculateBtn: {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    results: {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: `3px solid ${colors.bgGradient2}`,
    },
    resultsH3: {
      color: colors.primary,
      marginBottom: '1.5rem',
      textAlign: 'center' as const,
    },
    dtiDisplay: {
      background: 'white',
      borderRadius: '1rem',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    },
    dtiMain: {
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
    },
    dtiPercentage: {
      fontWeight: 800,
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    dtiLabel: {
      color: colors.textMuted,
      marginTop: '0.5rem',
    },
    dtiStatus: {
      display: 'inline-block',
      padding: '0.5rem 1.5rem',
      borderRadius: '2rem',
      fontWeight: 600,
      marginTop: '1rem',
    },
    dtiBreakdown: {
      display: 'grid',
      gap: '1rem',
      marginTop: '1.5rem',
    },
    breakdownItem: {
      background: '#f8fafc',
      padding: '1rem',
      borderRadius: '0.5rem',
      borderLeft: `4px solid ${colors.primary}`,
    },
    breakdownLabel: {
      fontSize: '0.85rem',
      color: colors.textMuted,
      marginBottom: '0.25rem',
    },
    breakdownValue: {
      fontSize: '1.3rem',
      fontWeight: 700,
      color: colors.textPrimary,
    },
    monthlySummary: {
      background: 'white',
      borderRadius: '1rem',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    },
    summaryTitle: {
      fontWeight: 700,
      color: colors.primary,
      marginBottom: '1rem',
    },
    summaryGrid: {
      display: 'grid',
      gap: '0.75rem',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.5rem 0',
      borderBottom: '1px solid #e2e8f0',
    },
    summaryRowLast: {
      borderBottom: 'none',
      fontWeight: 700,
      color: colors.primary,
      paddingTop: '1rem',
      borderTop: `2px solid ${colors.primary}`,
    },
    recommendations: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    },
    recommendationsH4: {
      color: colors.primary,
      marginBottom: '1rem',
    },
    recommendationList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    recommendationItem: {
      padding: '0.75rem',
      marginBottom: '0.5rem',
      background: '#f8fafc',
      borderRadius: '0.5rem',
      borderLeft: `4px solid ${colors.accent}`,
      fontSize: '0.95rem',
      color: colors.textSecondary,
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    list: {
      paddingLeft: '1.5rem',
      marginBottom: '1rem',
    },
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    strong: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
    },
    refLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    baseStyles.toolHeader,
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    baseStyles.toolHeaderH2,
    { fontSize: '1.4rem' },
    { fontSize: '1.6rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    baseStyles.calculatorForm,
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const sectionTitleStyle = getResponsiveStyle(
    baseStyles.sectionTitle,
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const formGridStyle = getResponsiveStyle(
    baseStyles.formGrid,
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const inputStyle = getResponsiveStyle(
    baseStyles.input,
    { padding: '0.75rem' },
    { padding: '0.75rem' },
    { padding: '0.75rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    baseStyles.calculateBtn,
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    baseStyles.results,
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    baseStyles.resultsH3,
    { fontSize: '1.4rem' },
    { fontSize: '1.45rem' },
    { fontSize: '1.5rem' }
  );

  const dtiDisplayStyle = getResponsiveStyle(
    baseStyles.dtiDisplay,
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const dtiPercentageStyle = getResponsiveStyle(
    baseStyles.dtiPercentage,
    { fontSize: '2.5rem' },
    { fontSize: '3rem' },
    { fontSize: '3.5rem' }
  );

  const dtiLabelStyle = getResponsiveStyle(
    baseStyles.dtiLabel,
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const dtiBreakdownStyle = getResponsiveStyle(
    baseStyles.dtiBreakdown,
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const monthlySummaryStyle = getResponsiveStyle(
    baseStyles.monthlySummary,
    { padding: '1.25rem' },
    { padding: '1.4rem' },
    { padding: '1.5rem' }
  );

  const summaryTitleStyle = getResponsiveStyle(
    baseStyles.summaryTitle,
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const recommendationsStyle = getResponsiveStyle(
    baseStyles.recommendations,
    { padding: '1.25rem' },
    { padding: '1.4rem' },
    { padding: '1.5rem' }
  );

  const recommendationsH4Style = getResponsiveStyle(
    baseStyles.recommendationsH4,
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const getStatusStyle = (statusClass: string) => {
    const baseStatusStyle = baseStyles.dtiStatus;
    switch (statusClass) {
      case 'excellent':
        return { ...baseStatusStyle, background: colors.statusExcellent, color: colors.statusExcellentText };
      case 'good':
        return { ...baseStatusStyle, background: colors.statusGood, color: colors.statusGoodText };
      case 'fair':
        return { ...baseStatusStyle, background: colors.statusFair, color: colors.statusFairText };
      case 'poor':
        return { ...baseStatusStyle, background: colors.statusPoor, color: colors.statusPoorText };
      default:
        return baseStatusStyle;
    }
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={toolHeaderH2Style}>🧮 {t('toolTitle')}</h2>
        </div>

        <form style={calculatorFormStyle} onSubmit={handleCalculate}>
          <div style={baseStyles.formSection}>
            <div style={sectionTitleStyle}>💰 {t('incomeSection')}</div>
            <div style={formGridStyle}>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('salary')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.salary}
                    onChange={(e) => handleInputChange('salary', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.salaryPeriod}
                    onChange={(e) => handleInputChange('salaryPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('pension')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.pension}
                    onChange={(e) => handleInputChange('pension', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.pensionPeriod}
                    onChange={(e) => handleInputChange('pensionPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('investment')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.investment}
                    onChange={(e) => handleInputChange('investment', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.investmentPeriod}
                    onChange={(e) => handleInputChange('investmentPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
                <span style={baseStyles.helperText}>{t('investmentHelper')}</span>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('otherIncome')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.otherIncome}
                    onChange={(e) => handleInputChange('otherIncome', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.otherIncomePeriod}
                    onChange={(e) => handleInputChange('otherIncomePeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
                <span style={baseStyles.helperText}>{t('otherIncomeHelper')}</span>
              </div>
            </div>
          </div>

          <div style={baseStyles.formSection}>
            <div style={sectionTitleStyle}>🏠 {t('housingSection')}</div>
            <div style={formGridStyle}>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('rental')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.rental}
                    onChange={(e) => handleInputChange('rental', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.rentalPeriod}
                    onChange={(e) => handleInputChange('rentalPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('mortgage')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.mortgage}
                    onChange={(e) => handleInputChange('mortgage', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.mortgagePeriod}
                    onChange={(e) => handleInputChange('mortgagePeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('propertyTax')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.propertyTax}
                    onChange={(e) => handleInputChange('propertyTax', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.propertyTaxPeriod}
                    onChange={(e) => handleInputChange('propertyTaxPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('hoa')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.hoa}
                    onChange={(e) => handleInputChange('hoa', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.hoaPeriod}
                    onChange={(e) => handleInputChange('hoaPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('insurance')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.insurance}
                    onChange={(e) => handleInputChange('insurance', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.insurancePeriod}
                    onChange={(e) => handleInputChange('insurancePeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div style={baseStyles.formSection}>
            <div style={sectionTitleStyle}>💳 {t('debtsSection')}</div>
            <div style={formGridStyle}>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('creditCards')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.creditCards}
                    onChange={(e) => handleInputChange('creditCards', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.creditCardsPeriod}
                    onChange={(e) => handleInputChange('creditCardsPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('studentLoan')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.studentLoan}
                    onChange={(e) => handleInputChange('studentLoan', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.studentLoanPeriod}
                    onChange={(e) => handleInputChange('studentLoanPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('autoLoan')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.autoLoan}
                    onChange={(e) => handleInputChange('autoLoan', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.autoLoanPeriod}
                    onChange={(e) => handleInputChange('autoLoanPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('otherLoan')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.otherLoan}
                    onChange={(e) => handleInputChange('otherLoan', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.otherLoanPeriod}
                    onChange={(e) => handleInputChange('otherLoanPeriod', e.target.value)}
                    style={baseStyles.select}
                  >
                    <option value="month">{t('monthly')}</option>
                    <option value="year">{t('yearly')}</option>
                  </select>
                </div>
                <span style={baseStyles.helperText}>{t('otherLoanHelper')}</span>
              </div>
            </div>
          </div>

          <button type="submit" style={calculateBtnStyle}>
            {t('calculateButton')}
          </button>
        </form>

        {results && (
          <div id="results" style={resultsStyle}>
            <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

            <div style={dtiDisplayStyle}>
              <div style={baseStyles.dtiMain}>
                <div style={dtiPercentageStyle}>{results.dtiPercentage}%</div>
                <div style={dtiLabelStyle}>{t('totalDTI')}</div>
                <div style={getStatusStyle(results.status.class)}>
                  {results.status.text}
                </div>
              </div>

              <div style={dtiBreakdownStyle}>
                <div style={baseStyles.breakdownItem}>
                  <div style={baseStyles.breakdownLabel}>{t('frontEndRatio')}</div>
                  <div style={baseStyles.breakdownValue}>{results.frontEndRatio}%</div>
                </div>
                <div style={baseStyles.breakdownItem}>
                  <div style={baseStyles.breakdownLabel}>{t('backEndRatio')}</div>
                  <div style={baseStyles.breakdownValue}>{results.backEndRatio}%</div>
                </div>
              </div>
            </div>

            <div style={monthlySummaryStyle}>
              <div style={summaryTitleStyle}>{t('monthlySummary')}</div>
              <div style={baseStyles.summaryGrid}>
                <div style={baseStyles.summaryRow}>
                  <span>{t('totalIncome')}</span>
                  <span>{results.totalIncome}</span>
                </div>
                <div style={baseStyles.summaryRow}>
                  <span>{t('housingExpenses')}</span>
                  <span>{results.housingExpenses}</span>
                </div>
                <div style={baseStyles.summaryRow}>
                  <span>{t('otherDebts')}</span>
                  <span>{results.otherDebts}</span>
                </div>
                <div style={{ ...baseStyles.summaryRow, ...baseStyles.summaryRowLast }}>
                  <span>{t('totalDebt')}</span>
                  <span>{results.totalDebt}</span>
                </div>
              </div>
            </div>

            <div style={recommendationsStyle}>
              <h4 style={recommendationsH4Style}>💡 {t('recommendationsTitle')}</h4>
              <ul style={baseStyles.recommendationList}>
                {results.recommendations.map((rec: string, index: number) => (
                  <li key={index} style={baseStyles.recommendationItem}>
                    💡 {rec}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introPara1')}</p>
        <p style={paragraphStyle}>{t('introPara2')}</p>
        <p style={paragraphStyle}>{t('introPara3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}>
            <strong style={baseStyles.strong}>{t('step1Title')}</strong> {t('step1Desc')}
          </li>
          <li style={baseStyles.listItem}>
            <strong style={baseStyles.strong}>{t('step2Title')}</strong> {t('step2Desc')}
          </li>
          <li style={baseStyles.listItem}>
            <strong style={baseStyles.strong}>{t('step3Title')}</strong> {t('step3Desc')}
          </li>
          <li style={baseStyles.listItem}>
            <strong style={baseStyles.strong}>{t('step4Title')}</strong> {t('step4Desc')}
          </li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insight1')}</p>
        <p style={paragraphStyle}>{t('insight2')}</p>
        <p style={paragraphStyle}>{t('insight3')}</p>
        <p style={paragraphStyle}>{t('insight4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('formulaTitle')}
        </h3>
        <p style={paragraphStyle}>{t('formulaDesc')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('ratioTypesTitle')}
        </h3>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>
            <strong style={baseStyles.strong}>{t('frontEndTitle')}</strong> {t('frontEndDesc')}
          </li>
          <li style={baseStyles.listItem}>
            <strong style={baseStyles.strong}>{t('backEndTitle')}</strong> {t('backEndDesc')}
          </li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('guidelinesTitle')}
        </h3>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('guideline1')}</li>
          <li style={baseStyles.listItem}>{t('guideline2')}</li>
          <li style={baseStyles.listItem}>{t('guideline3')}</li>
          <li style={baseStyles.listItem}>{t('guideline4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq1Q')}</h3>
          <p style={paragraphStyle}>{t('faq1A')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq2Q')}</h3>
          <p style={paragraphStyle}>{t('faq2A')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq3Q')}</h3>
          <p style={paragraphStyle}>{t('faq3A')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq4Q')}</h3>
          <p style={paragraphStyle}>{t('faq4A')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq5Q')}</h3>
          <p style={paragraphStyle}>{t('faq5A')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>
            <a href="https://www.westernsouthern.com/personal-finance/debt-to-income-ratio-calculator" target="_blank" rel="noopener noreferrer" style={baseStyles.refLink}>
              {t('ref1')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://www.salliemae.com/blog/debt-to-income-ratio/" target="_blank" rel="noopener noreferrer" style={baseStyles.refLink}>
              {t('ref2')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://bettermoneyhabits.bankofamerica.com/en/credit/what-is-debt-to-income-ratio" target="_blank" rel="noopener noreferrer" style={baseStyles.refLink}>
              {t('ref3')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://www.agsouthfc.com/news/blog/what-good-debt-income-ratio-and-how-calculate-yours" target="_blank" rel="noopener noreferrer" style={baseStyles.refLink}>
              {t('ref4')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://www.navyfederal.org/makingcents/credit-debt/debt-to-income-ratio.html" target="_blank" rel="noopener noreferrer" style={baseStyles.refLink}>
              {t('ref5')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://www.wellsfargo.com/goals-credit/debt-to-income-calculator/" target="_blank" rel="noopener noreferrer" style={baseStyles.refLink}>
              {t('ref6')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}